import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import {
  Box, Avatar, Typography, Button, Card, CardContent, Grid, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField
} from "@mui/material";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { db } from "../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";

const demoUser = {
  displayName: "ChessMaster",
  email: "user@email.com",
  bio: "Lover of chess and strategy games. Always up for a challenge!",
  avatarUrl: "",
  stats: {
    wins: 22,
    losses: 15,
    draws: 4,
    rating: 1450,
  },
  achievements: [
    { label: "10 Wins", icon: <EmojiEventsIcon color="warning" /> },
    { label: "First Checkmate", icon: <EmojiEventsIcon color="primary" /> },
    { label: "Played 20 Games", icon: <EmojiEventsIcon color="success" /> },
  ],
  recentGames: [
    { opponent: "Alice", result: "Win", date: "2025-07-30" },
    { opponent: "Bob", result: "Loss", date: "2025-07-28" },
    { opponent: "Carol", result: "Draw", date: "2025-07-25" },
  ]
};

export default function Profile() {
  const { currentUser } = useAuth();

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    displayName: currentUser?.displayName || "Anonymous",
    bio: "",
    avatarUrl: currentUser?.photoURL || "",
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    const userRef = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (userSnap) => {
      if (userSnap.exists()) {
        setUser(userSnap.data());
      }
    }, (error) => {
      console.error("Error fetching user stats:", error);
    });
    return () => unsubscribe();
  }, [currentUser]);

  if (!currentUser) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Please log in to view your profile.</Typography>
      </Box>
    );
  }

  const handleEditOpen = () => setEditOpen(true);
  const handleEditClose = () => setEditOpen(false);

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = () => {
    // Save logic here (API call)
    setEditOpen(false);
  };

  const wins = user?.stats?.wins ?? 0;
  const losses = user?.stats?.losses ?? 0;
  const draws = user?.stats?.draws ?? 0;
  const rating = user?.stats?.rating ?? 1200;
  const totalGames = wins + losses + draws;
  const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", mt: 6, p: 3 }}>
      {/* Profile Header */}
      <Card sx={{ mb: 4, p: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={form.avatarUrl ? form.avatarUrl : undefined}
              sx={{ width: 90, height: 90, fontSize: 40, bgcolor: "#b58863" }}
            >
              {!form.avatarUrl && (form.displayName ? form.displayName[0] : "")}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight={700}>{form.displayName}</Typography>
            <Typography variant="subtitle1" color="text.secondary">{currentUser.email}</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>{form.bio || "No bio provided."}</Typography>
            <Button variant="outlined" size="small" sx={{ mt: 2 }} onClick={handleEditOpen}>Edit Profile</Button>
          </Grid>
        </Grid>
      </Card>

      {/* Chess Stats */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Chess Stats</Typography>
          {totalGames === 0 ? (
            <Typography color="text.secondary">No games played yet.</Typography>
          ) : (
            <Grid container spacing={2} alignItems="center">
              <Grid item><Typography color="#388e3c">Wins<br />{wins}</Typography></Grid>
              <Grid item><Typography color="#d32f2f">Losses<br />{losses}</Typography></Grid>
              <Grid item><Typography color="#fbc02d">Draws<br />{draws}</Typography></Grid>
              <Grid item><Typography color="#1976d2">Rating<br />{rating}</Typography></Grid>
              <Grid item><Typography>Win Rate<br />{winRate}%</Typography></Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Achievements */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Achievements</Typography>
          {user?.achievements && user?.achievements.length > 0 ? (
            user?.achievements.map((ach, idx) => (
              <Chip
                key={idx}
                icon={ach.icon || <EmojiEventsIcon />}
                label={ach.label}
                sx={{ mr: 1, mb: 1 }}
              />
            ))
          ) : (
            <Typography color="text.secondary">No achievements earned yet.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Recent Games */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Recent Games</Typography>
          {user?.recentGames && user?.recentGames.length > 0 ? (
            user?.recentGames.map((game, idx) => (
              <Grid container key={idx} spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs={4}><Typography>{game.opponent}</Typography></Grid>
                <Grid item xs={4}>
                  <Typography color={
                    game.result === "Win" ? "#388e3c" :
                    game.result === "Loss" ? "#d32f2f" :
                    "#fbc02d"
                  }>
                    {game.result}
                  </Typography>
                </Grid>
                <Grid item xs={4}><Typography>{game.date}</Typography></Grid>
              </Grid>
            ))
          ) : (
            <Typography color="text.secondary">No recent games.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose}>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Display Name"
            name="displayName"
            value={form.displayName}
            onChange={handleFormChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Avatar URL"
            name="avatarUrl"
            value={form.avatarUrl}
            onChange={handleFormChange}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Bio"
            name="bio"
            value={form.bio}
            onChange={handleFormChange}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}